import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Calendar,
  Search,
  ArrowRight,
  SlidersHorizontal,
  X,
  SearchX,
  Star,
  AlertCircle,
} from 'lucide-react';
import api from '../../../Api.js';
import './HoneymoonPackages.css';

/* ─── API ────────────────────────────────────────────────────── */
const API_TOKEN =
  '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5';

const getPackages = async (params = {}) => {
  const res = await api.get('/honeymoon/packages', {
    params,
    headers: { 'X-API-TOKEN': API_TOKEN },
  });
  return res.data.data;
};

const getDestinations = async () => {
  const res = await api.get('/destinations', {
    headers: { 'X-API-TOKEN': API_TOKEN },
  });
  return res.data.data;
};

/* ─── Helpers ───────────────────────────────────────────────── */
const formatINR = (value) => {
  const num = Number(value);
  if (!num || Number.isNaN(num)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `₹${num}`;
  }
};

/* ─── Search Bar ────────────────────────────────────────────── */
const PackageSearchBar = ({ location, setLocation, date, setDate, onSearch }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(location, date);
  };

  return (
    <form className="hmp-searchbar" onSubmit={handleSubmit}>
      <div className="hmp-searchbar-field">
        <MapPin size={18} className="hmp-searchbar-icon" aria-hidden="true" />
        <input
          type="text"
          placeholder="Where do you want to go?"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          aria-label="Destination"
        />
      </div>

      <span className="hmp-searchbar-divider" aria-hidden="true" />

      <div className="hmp-searchbar-field hmp-searchbar-field-date">
        <Calendar size={18} className="hmp-searchbar-icon" aria-hidden="true" />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          aria-label="Travel date"
        />
      </div>

      <button type="submit" className="hmp-searchbar-btn">
        <Search size={16} aria-hidden="true" />
        <span>Search</span>
      </button>
    </form>
  );
};

/* ─── Filter Sidebar (unified state + city list) ─────────────── */
const FilterSidebar = ({
  destinations,
  selectedOption,
  onSelect,
  onReset,
  loading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  /* Build a flat list of options:
     - State entry:        label = "West Bengal"
     - City entry:         label = "Kolkata, West Bengal"  */
  const allOptions = useMemo(() => {
    const list = [];
    (destinations || []).forEach((state) => {
      // State-level entry (sends only destination_id)
      list.push({
        key: `state-${state.id}`,
        type: 'state',
        stateId: state.id,
        cityId: null,
        cityName: null,
        stateName: state.name,
        searchText: state.name.toLowerCase(),
      });

      // City-level entries (send city_id + destination_id)
      (state.cities || []).forEach((city) => {
        list.push({
          key: `city-${city.id}`,
          type: 'city',
          stateId: state.id,
          cityId: city.id,
          cityName: city.name,
          stateName: state.name,
          searchText: `${city.name} ${state.name}`.toLowerCase(),
        });
      });
    });
    return list;
  }, [destinations]);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return allOptions;
    const lower = searchTerm.toLowerCase();
    return allOptions.filter((o) => o.searchText.includes(lower));
  }, [allOptions, searchTerm]);

  const isSelected = (opt) =>
    !!selectedOption &&
    selectedOption.type === opt.type &&
    selectedOption.stateId === opt.stateId &&
    selectedOption.cityId === opt.cityId;

  return (
    <div className="hmp-filters">
      <div className="hmp-filters-head">
        <div>
          <h3 className="hmp-filters-title">Filters</h3>
          <p className="hmp-filters-hint">Refine your search</p>
        </div>
        <button type="button" className="hmp-filters-reset" onClick={onReset}>
          Reset
        </button>
      </div>

      <div className="hmp-filters-search">
        <Search size={15} className="hmp-filters-search-icon" aria-hidden="true" />
        <input
          type="text"
          placeholder="Search state or city…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Search filters"
        />
        {searchTerm && (
          <button
            type="button"
            className="hmp-filters-search-clear"
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="hmp-filters-group">
        <h4 className="hmp-filters-label">States &amp; Cities</h4>

        {loading ? (
          <p className="hmp-filters-msg">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="hmp-filters-msg">No matching states or cities</p>
        ) : (
          <ul className="hmp-filters-list">
            {filtered.map((opt) => {
              const active = isSelected(opt);
              return (
                <li key={opt.key}>
                  <button
                    type="button"
                    className={`hmp-filters-option ${active ? 'is-selected' : ''}`}
                    onClick={() => onSelect(opt)}
                    aria-pressed={active}
                  >
                    <span className="hmp-filters-radio" aria-hidden="true" />

                    <span className="hmp-filters-option-text">
                      {opt.type === 'state' ? (
                        <span className="hmp-opt-state">{opt.stateName}</span>
                      ) : (
                        <>
                          <span className="hmp-opt-city">{opt.cityName}</span>
                          <span className="hmp-opt-comma">,</span>
                          <span className="hmp-opt-suffix">{opt.stateName}</span>
                        </>
                      )}
                    </span>

                    {opt.type === 'state' && (
                      <span className="hmp-opt-tag">State</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ─── Mobile Drawer ────────────────────────────────────────── */
const MobileFilterDrawer = ({ open, onClose, ...props }) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => (document.body.style.overflow = '');
  }, [open]);

  return (
    <>
      <div
        className={`hmp-drawer-backdrop ${open ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`hmp-drawer ${open ? 'is-open' : ''}`}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? 'true' : undefined}
        aria-label="Filters"
      >
        <span className="hmp-drawer-grip" aria-hidden="true" />

        <div className="hmp-drawer-head">
          <h3 className="hmp-drawer-title">Filters</h3>
          <button
            type="button"
            className="hmp-drawer-close"
            onClick={onClose}
            aria-label="Close filters"
          >
            <X size={20} />
          </button>
        </div>

        <div className="hmp-drawer-body">
          <FilterSidebar {...props} />
        </div>

        <div className="hmp-drawer-footer">
          <button type="button" className="hmp-drawer-apply" onClick={onClose}>
            Apply Filters
          </button>
        </div>
      </aside>
    </>
  );
};

/* ─── Package Card ─────────────────────────────────────────── */
const PackageCard = ({ package: pkg, index = 0 }) => (
  <motion.article
    className="hmp-card"
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.06 }}
  >
    <div className="hmp-card-media">
      <img
        src={pkg.featured_photo_url || 'https://via.placeholder.com/600x400?text=No+Image'}
        alt={pkg.name}
        className="hmp-card-img"
        loading="lazy"
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/600x400?text=No+Image';
        }}
      />
      <span className="hmp-card-veil" aria-hidden="true" />

      <span className="hmp-card-badge">
        {pkg.duration_days}D / {pkg.duration_nights}N
      </span>

      {pkg.destination && (
        <span className="hmp-card-loc">
          <MapPin size={12} aria-hidden="true" />
          {pkg.destination.name}
        </span>
      )}
    </div>

    <div className="hmp-card-body">
      <h3 className="hmp-card-title">{pkg.name}</h3>
      <p className="hmp-card-desc">{pkg.short_description}</p>

      <div className="hmp-card-price">
        <span className="hmp-card-price-label">Starting from</span>
        <span className="hmp-card-price-value">
          {formatINR(pkg.plans_starting_from)}
          <span className="hmp-card-price-unit">/ couple</span>
        </span>
      </div>

      <Link to={`/honeymoon/packages/${pkg.slug}`} className="hmp-card-cta">
        View Package
        <ArrowRight size={15} aria-hidden="true" />
      </Link>
    </div>
  </motion.article>
);

/* ─── Skeleton ─────────────────────────────────────────────── */
const PackageSkeleton = () => (
  <div className="hmp-skeleton" aria-hidden="true">
    <div className="hmp-skeleton-media" />
    <div className="hmp-skeleton-body">
      <div className="hmp-skeleton-line hmp-skeleton-title" />
      <div className="hmp-skeleton-line hmp-skeleton-loc" />
      <div className="hmp-skeleton-line hmp-skeleton-desc" />
      <div className="hmp-skeleton-line hmp-skeleton-price" />
      <div className="hmp-skeleton-line hmp-skeleton-btn" />
    </div>
  </div>
);

/* ─── Empty State ──────────────────────────────────────────── */
const EmptyState = ({ onReset }) => (
  <div className="hmp-empty">
    <span className="hmp-empty-icon">
      <SearchX size={30} aria-hidden="true" />
    </span>
    <h3 className="hmp-empty-title">No Honeymoon Packages Found</h3>
    <p className="hmp-empty-text">
      We couldn’t find any romantic getaways matching your preferences. Try
      another destination or reset your filters.
    </p>
    <div className="hmp-empty-actions">
      <button type="button" className="hmp-btn-ghost" onClick={onReset}>
        Clear Filters
      </button>
      <button type="button" className="hmp-btn-gold" onClick={onReset}>
        Explore All Packages
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Main Page
   ══════════════════════════════════════════════════════════ */
const HoneymoonPackages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null); // { type, stateId, cityId, cityName, stateName }
  const [searchLocation, setSearchLocation] = useState(
    searchParams.get('location') || ''
  );
  const [travelDate, setTravelDate] = useState(searchParams.get('date') || '');
  const [sortOption, setSortOption] = useState('recommended');
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const sortPackages = useCallback((list, sort) => {
    const copy = [...list];
    switch (sort) {
      case 'price_low':
        return copy.sort((a, b) => a.plans_starting_from - b.plans_starting_from);
      case 'price_high':
        return copy.sort((a, b) => b.plans_starting_from - a.plans_starting_from);
      case 'duration_short':
        return copy.sort((a, b) => a.duration_days - b.duration_days);
      case 'duration_long':
        return copy.sort((a, b) => b.duration_days - a.duration_days);
      default:
        return copy;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPackages();
      setPackages(data);
      setFiltered(sortPackages(data, sortOption));
    } catch (e) {
      console.error(e);
      setError('Unable to load honeymoon packages. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortPackages, sortOption]);

  /* Fetch by unified option:
     - state → destination_id only
     - city  → city_id AND destination_id */
  const fetchByOption = useCallback(
    async (opt) => {
      setFilterLoading(true);
      setError(null);
      try {
        const params = {};
        if (opt.type === 'state') {
          params.destination_id = opt.stateId;
        } else if (opt.type === 'city') {
          params.city_id = opt.cityId;
          params.destination_id = opt.stateId;
        }
        const data = await getPackages(params);
        setPackages(data);
        setFiltered(sortPackages(data, sortOption));
      } catch (e) {
        console.error(e);
        setError('Unable to load packages for this selection.');
      } finally {
        setFilterLoading(false);
      }
    },
    [sortPackages, sortOption]
  );

  const loadDestinations = useCallback(async () => {
    try {
      const data = await getDestinations();
      setDestinations(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  /* Initial load */
  useEffect(() => {
    loadDestinations();
    const loc = searchParams.get('location');
    if (loc) {
      const fetchAndFilter = async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await getPackages();
          setPackages(data);
          const lower = loc.toLowerCase();
          const list = data.filter(
            (p) =>
              p.name?.toLowerCase().includes(lower) ||
              p.destination?.name?.toLowerCase().includes(lower) ||
              p.short_description?.toLowerCase().includes(lower)
          );
          setFiltered(sortPackages(list, sortOption));
        } catch (e) {
          console.error(e);
          setError('Unable to load packages.');
        } finally {
          setLoading(false);
        }
      };
      fetchAndFilter();
    } else {
      fetchAll();
    }
    // eslint-disable-next-line
  }, []);

  /* Re-sort on sort change */
  useEffect(() => {
    if (!loading && !filterLoading)
      setFiltered((prev) => sortPackages(prev, sortOption));
  }, [sortOption, sortPackages, loading, filterLoading]);

  /* Handlers */
  const handleSearch = (loc, date) => {
    setSelectedOption(null);
    const params = new URLSearchParams();
    if (loc) params.set('location', loc);
    if (date) params.set('date', date);
    setSearchParams(params);
    if (!loc.trim()) {
      fetchAll();
      return;
    }
    const perform = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPackages();
        setPackages(data);
        const lower = loc.toLowerCase().trim();
        const list = data.filter(
          (p) =>
            p.name?.toLowerCase().includes(lower) ||
            p.destination?.name?.toLowerCase().includes(lower) ||
            p.short_description?.toLowerCase().includes(lower)
        );
        setFiltered(sortPackages(list, sortOption));
      } catch (e) {
        console.error(e);
        setError('Search failed.');
      } finally {
        setLoading(false);
      }
    };
    perform();
  };

  /* Unified select — toggle behavior */
  const handleSelect = (opt) => {
    const same =
      selectedOption &&
      selectedOption.type === opt.type &&
      selectedOption.stateId === opt.stateId &&
      selectedOption.cityId === opt.cityId;

    if (same) {
      setSelectedOption(null);
      fetchAll();
      return;
    }
    setSelectedOption(opt);
    fetchByOption(opt);
  };

  const handleReset = () => {
    setSelectedOption(null);
    setSearchLocation('');
    setTravelDate('');
    setSortOption('recommended');
    setSearchParams({});
    fetchAll();
    setMobileOpen(false);
  };

  const isFilterActive = !!selectedOption || !!searchLocation;

  return (
    <div className="hmp-page">
      <span className="hmp-orb hmp-orb-a" aria-hidden="true" />
      <span className="hmp-orb hmp-orb-b" aria-hidden="true" />

      <div className="hmp-shell">
        {/* ---------- Breadcrumb ---------- */}
        <nav className="hmp-breadcrumb" aria-label="Breadcrumb">
          <Link to="/honeymoon">Home</Link>
          <span aria-hidden="true">/</span>
          <span className="hmp-breadcrumb-current">Honeymoon Packages</span>
        </nav>

        {/* ---------- Hero ---------- */}
        <motion.header
          className="hmp-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >

          <h1 className="hmp-title">Honeymoon Packages</h1>
          <p className="hmp-subtitle">Find your perfect romantic getaway</p>
        </motion.header>

        {/* ---------- Layout ---------- */}
        <div className="hmp-layout">
          {/* Desktop sidebar */}
          <aside className="hmp-sidebar">
            <FilterSidebar
              destinations={destinations}
              selectedOption={selectedOption}
              onSelect={handleSelect}
              onReset={handleReset}
              loading={loading || filterLoading}
            />
          </aside>

          {/* Results */}
          <section className="hmp-results">
            <PackageSearchBar
              location={searchLocation}
              setLocation={setSearchLocation}
              date={travelDate}
              setDate={setTravelDate}
              onSearch={handleSearch}
            />

            <div className="hmp-results-head">
              <p className="hmp-results-count">
                {loading ? (
                  'Loading packages…'
                ) : (
                  <>
                    <strong>{filtered.length}</strong>{' '}
                    {filtered.length === 1 ? 'package' : 'packages'} found
                    {searchLocation && (
                      <span className="hmp-results-query">
                        {' '}for “{searchLocation}”
                      </span>
                    )}
                    {isFilterActive && !searchLocation && selectedOption && (
                      <span className="hmp-results-chip">
                        {selectedOption.type === 'city'
                          ? `${selectedOption.cityName}, ${selectedOption.stateName}`
                          : selectedOption.stateName}
                      </span>
                    )}
                  </>
                )}
              </p>

              <div className="hmp-results-actions">
                <div className="hmp-sort">
                  <label htmlFor="hmp-sort" className="hmp-sort-label">
                    Sort by
                  </label>
                  <select
                    id="hmp-sort"
                    className="hmp-sort-select"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <option value="recommended">Recommended</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                    <option value="duration_short">Duration: Shortest</option>
                    <option value="duration_long">Duration: Longest</option>
                  </select>
                </div>

                <button
                  type="button"
                  className="hmp-mobile-toggle"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open filters"
                >
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  <span className="hmp-mobile-toggle-text">Filters</span>
                  {isFilterActive && <span className="hmp-mobile-dot" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="hmp-error">
                <span className="hmp-error-icon">
                  <AlertCircle size={20} aria-hidden="true" />
                </span>
                <p className="hmp-error-text">{error}</p>
                <button
                  type="button"
                  className="hmp-btn-gold hmp-error-retry"
                  onClick={fetchAll}
                >
                  Try Again
                </button>
              </div>
            )}

            {!error && (
              <>
                {loading || filterLoading ? (
                  <div className="hmp-grid">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <PackageSkeleton key={i} />
                    ))}
                  </div>
                ) : filtered.length === 0 ? (
                  <EmptyState onReset={handleReset} />
                ) : (
                  <div className="hmp-grid">
                    {filtered.map((p, i) => (
                      <PackageCard key={p.id} package={p} index={i} />
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {/* ---------- Mobile drawer ---------- */}
      <MobileFilterDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        destinations={destinations}
        selectedOption={selectedOption}
        onSelect={handleSelect}
        onReset={handleReset}
        loading={loading || filterLoading}
      />
    </div>
  );
};

export default HoneymoonPackages;